// src/pages/Home.tsx
import React, { useMemo, useState } from 'react';
import { motion, Variants } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Zap, Moon } from 'lucide-react';

import { STYLES } from '../theme/styles';
import { COLORS } from '../theme/colors';

// CONTEXTS
import { useAuth } from '@/contexts/AuthContext';
import { useEvents } from '@/contexts/EventsContext';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import { useClients } from '@/contexts/ClientsContext';
import { useUsers } from '@/contexts/UsersContext';

// COMPONENTI UI
import PrimaryButton from '@/components/ui/PrimaryButton';
import HomeHero from '@/components/home/HomeHero';
import HomeAttention, { AtRiskClient } from '@/components/home/HomeAttention';
import HomeGoalChecks from '@/components/home/HomeGoalChecks';
import HomeRevenueCard from '@/components/home/HomeRevenueCard';
import HomeWeeklyActivity from '@/components/home/HomeWeeklyActivity';
import { VISIBILITY_PACKAGES } from '@/components/billing/constants';

// MODALS
import NewAppointmentModal from '@/components/modals/NewAppointmentModal';
import HomeEndOfDay, { StaffProduction } from '@/components/home/HomeEndOfDay';

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants: Variants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { type: 'spring', stiffness: 50 } },
};

const Home: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // CONTEXT
  const { organization, user, visibility, isAdmin, canSeeAllAgenda, canSeeFinancials } = useAuth();
  const { bookings: allBookings } = useEvents();
  const { clients } = useClients();
  const { getUserById } = useUsers();
  const {
    filterDataByPeriod,
    calculateRevenue,
    calculateWeeklyDistribution,
    getValidBookings,
    getBookingsByValidHairdressers,
    getPreviousPeriodData
  } = useAnalytics();

  // MODAL STATES
  const [isNewAppointmentOpen, setIsNewAppointmentOpen] = useState(false);
  const [isEndOfDayOpen, setIsEndOfDayOpen] = useState(false);

  // ====== LOGICA ======

  // 1) Prenotazioni filtrate per hairdresser validi (e per user corrente se non admin)
  const bookings = useMemo(() => {
    let b = getBookingsByValidHairdressers(allBookings);
    // Junior vede solo i propri appuntamenti; owner e senior vedono tutta l'agenda.
    if (!canSeeAllAgenda && user) {
      b = b.filter((booking: any) => booking.hairdresser_id === user.id);
    }
    return b;
  }, [allBookings, getBookingsByValidHairdressers, canSeeAllAgenda, user]);

  // 2) Prenotazioni valide
  const validBookings = useMemo(
    () => getValidBookings(bookings),
    [bookings, getValidBookings]
  );

  // 3) Prenotazioni valide di oggi
  const validDailyBookings = useMemo(
    () => filterDataByPeriod(validBookings, 'day', 'bookings'),
    [validBookings, filterDataByPeriod]
  );

  const previousDailyBookings = useMemo(
    () => getPreviousPeriodData(validBookings, 'day', 'bookings'),
    [validBookings, getPreviousPeriodData]
  );

  // 4) Incasso giornaliero
  const currentDailyRevenue = useMemo(
    () => calculateRevenue(validDailyBookings),
    [validDailyBookings, calculateRevenue]
  );

  const previousDailyRevenue = useMemo(
    () => calculateRevenue(previousDailyBookings),
    [previousDailyBookings, calculateRevenue]
  );

  const dailyBookingsCount = useMemo(
    () => validDailyBookings.length,
    [validDailyBookings]
  );

  // 5) Dati settimanali (per grafici - BARCHART PRENOTAZIONI)
  const weeklyData = useMemo(
    () => calculateWeeklyDistribution(validBookings, 'current_week', 'bookings'),
    [validBookings, calculateWeeklyDistribution]
  );

  const weeklyBookingsTotal = useMemo(
    () => weeklyData.reduce((sum: number, d: any) => sum + (d.value ?? 0), 0),
    [weeklyData]
  );

  // Usata per grafico fatturato (area chart - REVENUE)
  const weeklyRevenue = useMemo(
    () => calculateWeeklyDistribution(validBookings, 'current_week', 'revenue'),
    [validBookings, calculateWeeklyDistribution]
  );
  const revenueSeries = weeklyRevenue;

  // Boost visibilità attivo (per il pill "Get Boost" nel titolo)
  const activeBoost = useMemo(() => {
    if (!visibility || new Date(visibility.end_date) < new Date()) return null;
    return VISIBILITY_PACKAGES.find((pkg) => pkg.weight === visibility.priority_weight);
  }, [visibility]);

  // Clienti a rischio: hanno un'ultima visita passata "vecchia" (tra 40 e 365 giorni fa)
  // e nessun appuntamento futuro già in agenda → vanno richiamati.
  const atRiskClients: AtRiskClient[] = useMemo(() => {
    const DAY = 86400000;
    const MIN_DAYS = 40;
    const MAX_DAYS = 365;
    const now = new Date();
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    return (clients || [])
      .map((c: any): AtRiskClient | null => {
        const cb = (allBookings || []).filter(
          (b: any) => b.client_id === c.id && b.date && b.status !== 'cancelled'
        );
        if (!cb.length) return null;

        // Se ha un appuntamento futuro, non è a rischio (sta già tornando)
        const hasFuture = cb.some((b: any) => new Date(b.date) >= todayStart);
        if (hasFuture) return null;

        const lastDate = cb.reduce((a: any, b: any) => (a.date > b.date ? a : b)).date;
        const days = Math.floor((now.getTime() - new Date(lastDate).getTime()) / DAY);
        if (days < MIN_DAYS || days > MAX_DAYS) return null;

        const name =
          c.full_name || [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Cliente';
        return { id: String(c.id), name, phone: c.phone, days };
      })
      .filter((c): c is AtRiskClient => c !== null)
      .sort((a, b) => b.days - a.days);
  }, [clients, allBookings]);

  // ===== FINE GIORNATA (riepilogo read-only) =====
  const staffProduction: StaffProduction[] = useMemo(() => {
    const byStaff = new Map<string, any[]>();
    validDailyBookings.forEach((b: any) => {
      const key = String(b.hairdresser_id ?? 'unknown');
      if (!byStaff.has(key)) byStaff.set(key, []);
      byStaff.get(key)!.push(b);
    });
    return Array.from(byStaff.entries())
      .map(([id, bs]) => {
        const u = getUserById(id);
        const name = u
          ? `${(u as any).first_name ?? ''} ${(u as any).last_name ?? ''}`.trim() || 'Staff'
          : 'Staff';
        return { id, name, revenue: calculateRevenue(bs), count: bs.length };
      })
      .sort((a, b) => b.revenue - a.revenue);
  }, [validDailyBookings, getUserById, calculateRevenue]);

  const tomorrowBookings = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() + 1);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return validBookings.filter((b: any) => {
      const d = new Date(b.date);
      return d >= start && d < end;
    });
  }, [validBookings]);

  const tomorrowRevenue = useMemo(
    () => calculateRevenue(tomorrowBookings),
    [tomorrowBookings, calculateRevenue]
  );

  const noShowCount = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return bookings.filter((b: any) => {
      const d = new Date(b.date);
      return b.status === 'no_show' && d >= start && d < end;
    }).length;
  }, [bookings]);

  // ====== RENDER ======

  return (
    <motion.div
      className={STYLES.pageContainer}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* HEADER */}
      <motion.div variants={itemVariants} className={STYLES.pageHeader.wrapper}>
        <div className="min-w-0">
          <div className="flex items-start justify-between gap-3">
            <h1 className={`${STYLES.pageHeader.title} min-w-0 flex flex-wrap items-center gap-x-4 gap-y-2`}>
              {organization?.name || t('salon.title')}
              {isAdmin && (
              <button
                onClick={() => navigate('/billing', { state: { initialTab: 'visibility' } })}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-clip-500 to-orange-500 text-white text-xs font-bold rounded-full shadow-lg shadow-clip-200/50 hover:opacity-90 transition-opacity animate-pulse-slow whitespace-nowrap"
              >
                {activeBoost ? (
                  <>
                    <activeBoost.icon size={14} className="text-white" />
                    {t(activeBoost.title)}
                  </>
                ) : (
                  <>
                    <Zap size={14} />
                    {t('home.get_boost')}
                  </>
                )}
              </button>
              )}
            </h1>

            {/* Su mobile il riepilogo sta accanto al titolo: da solo su una riga
                si allargava per tutta la schermata senza motivo. */}
            {canSeeFinancials && (
              <button
                type="button"
                onClick={() => setIsEndOfDayOpen(true)}
                aria-label="Fine giornata"
                title="Fine giornata"
                className="md:hidden flex-shrink-0 p-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 shadow-sm hover:bg-gray-50 active:scale-95 transition-all"
              >
                <Moon size={20} />
              </button>
            )}
          </div>
          <p className={STYLES.pageHeader.subtitle}>{t('home.subtitle')}</p>
        </div>

        {canSeeFinancials && (
          <div className="hidden md:block">
            <PrimaryButton
              variant="secondary"
              icon={Moon}
              label="Fine giornata"
              onClick={() => setIsEndOfDayOpen(true)}
            />
          </div>
        )}
      </motion.div>

      {/* VERIFICHE PERIODICHE OBIETTIVI (solo titolare: dati economici) */}
      {canSeeFinancials && (
        <motion.div variants={itemVariants}>
          <HomeGoalChecks />
        </motion.div>
      )}

      {/* RIGA 1: HERO NUOVO APPUNTAMENTO + INCASSI DI OGGI */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-stretch">
        <motion.div variants={itemVariants} className={canSeeFinancials ? 'md:col-span-8' : 'md:col-span-12'}>
          <HomeHero
            onNewAppointment={() => setIsNewAppointmentOpen(true)}
            onViewAgenda={() => navigate('/calendar')}
          />
        </motion.div>

        {canSeeFinancials && (
          <motion.div variants={itemVariants} className="md:col-span-4">
            <HomeRevenueCard
              revenueToday={currentDailyRevenue || 0}
              revenuePrevious={previousDailyRevenue || 0}
              bookingsToday={dailyBookingsCount}
              series={revenueSeries}
              colors={COLORS}
            />
          </motion.div>
        )}
      </div>

      {/* RIGA 2: ATTIVITÀ SETTIMANALE (1/3) + RICHIEDE ATTENZIONE (2/3) */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-stretch">
        <motion.div variants={itemVariants} className={canSeeAllAgenda ? 'md:col-span-4' : 'md:col-span-12'}>
          <HomeWeeklyActivity
            weeklyData={weeklyData}
            weeklyTotal={weeklyBookingsTotal}
            colors={COLORS}
          />
        </motion.div>

        {canSeeAllAgenda && (
          <motion.div variants={itemVariants} className="md:col-span-8">
            <HomeAttention atRisk={atRiskClients} />
          </motion.div>
        )}
      </div>

      {/* MODAL NUOVO APPUNTAMENTO */}
      <NewAppointmentModal
        isOpen={isNewAppointmentOpen}
        onClose={() => setIsNewAppointmentOpen(false)}
      />

      {/* MODAL FINE GIORNATA */}
      <HomeEndOfDay
        isOpen={isEndOfDayOpen}
        onClose={() => setIsEndOfDayOpen(false)}
        todayRevenue={currentDailyRevenue || 0}
        todayCount={dailyBookingsCount}
        perStaff={staffProduction}
        tomorrowCount={tomorrowBookings.length}
        tomorrowRevenue={tomorrowRevenue || 0}
        noShowCount={noShowCount}
      />
    </motion.div>
  );
};

export default Home;
